import { useMemo } from 'react';
import { AlertTriangle, LogIn } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';

/** True when the app is running inside an iframe (CRM embed). */
const isInIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();

/**
 * Account mismatch warning — only shown when embedded in an iframe.
 * Offers:
 *  1. "Login again" — signs out so AuthGuard shows its inline login form
 *  2. "I understand the risk, continue" to dismiss
 */
export function AccountMismatchBanner() {
  const user = useAuthStore((s) => s.user);
  const suggestedEmail = useAuthStore((s) => s.suggestedLoginEmail);
  const dismiss = useAuthStore((s) => s.dismissAccountMismatch);
  const signOut = useAuthStore((s) => s.signOut);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS) ?? '';

  const platformLabel = useMemo(() => {
    const code = appCodeSAAS.toLowerCase();
    if (code.includes('desk') || code.includes('zendesk') || code.includes('freshdesk')) return 'helpdesk';
    return 'CRM';
  }, [appCodeSAAS]);

  if (!isInIframe) return null;
  if (!user?.email || !suggestedEmail) return null;

  const userEmail = user.email.toLowerCase();
  if (userEmail === suggestedEmail) return null;

  // Check if user previously dismissed this mismatch
  try {
    const dismissed = localStorage.getItem(`ua_ignore_account_mismatch_for_${userEmail}`);
    if (dismissed === suggestedEmail) return null;
  } catch { /* continue showing */ }

  const handleLoginAgain = async () => {
    // `reload: false` keeps the SPA alive: clearing `user` makes AuthGuard
    // swap this whole subtree for its own InlineLoginForm (pre-filled from
    // suggestedLoginEmail, which survives signOut), so the user re-logs in
    // without a redirect. signOut still disposes the previous account's
    // modules and API caches, which is what a reload would have done.
    await signOut({ reload: false });
  };


  // Mismatch warning banner with action buttons
  return (
    // `z-50` alone was inert (no positioning) and would lose to the auth
    // overlays anyway — SaaSAuthPrompt is `fixed inset-0 z-[60]`. Matches
    // AccountHandoffBanner so both warnings survive an authorize prompt.
    <div className="relative z-[70] flex flex-col items-center gap-2 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs font-medium py-3 px-4 shrink-0">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>
          Account mismatch — {platformLabel} user is <strong>{suggestedEmail}</strong>, but you're signed in as <strong>{user.email}</strong>
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={handleLoginAgain}
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 dark:bg-amber-500 text-white px-3 py-1 text-xs font-medium hover:bg-amber-700 dark:hover:bg-amber-600 transition-colors"
        >
          <LogIn className="h-3 w-3" />
          Login again
        </button>
        <button
          onClick={dismiss}
          className="text-xs text-amber-700/70 dark:text-amber-300/70 hover:text-amber-800 dark:hover:text-amber-200 underline underline-offset-2 transition-colors"
        >
          I understand the risk, continue
        </button>
      </div>
    </div>
  );
}
